import { AfterViewInit, Component, Inject, Input, ViewChild } from '@angular/core';

import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';

import { ConfirmationDialogComponent } from '../../../shared/dialogs/components/confirmation-dialog.component';
import { CommandRelay } from '../../../shared/commands/command-relay.class';
import { JSR310ConversionService } from '../../../shared/jsr310-localdates/services/jsr310-conversion.service';

import { MemoService } from '../services/memo.service';
import { Memo } from '../entities/memo.entity';

import { StaticTypeService } from '../../../static-types/services/static-types.service';
import { MemoCategory } from '../../../static-types/entities/memo-category.entity';
import { MemoSubCategory } from '../../../static-types/entities/memo-sub-category.entity';
import { MemoLevel } from '../../../static-types/entities/memo-level.entity';

import { ParcTLR } from '../../structure/parc/entities/parc-tlr.entity';
import { ParcService } from '../../structure/parc/services/parc.service';

import { OuvrageTLR } from '../../structure/ouvrage/entities/ouvrage-tlr.entity';
import { OuvrageService } from '../../structure/ouvrage/services/ouvrage.service';

import { MemosFilters } from '../entities/memos-filters.entity';

import { EditMemoDialogComponent } from './edit-memo-dialog.component';

@Component({
	selector: 'memos-table',
	templateUrl: 'memos-table.html',
	styleUrls: ['memos-table.css']
})
export class MemosTableComponent implements AfterViewInit {
	// Elements de fonctionnement du tableau des memo
	maxSize = 100;
	isLoading: boolean = true;
	displayedColumns: string[] = ['intitule', 'categorie', 'niveau', 'parc', 'ouvrages', 'dateDebut', 'dateFin', 'commentaire'];
	data: Memo[] = [];
	maxSizeExceeded = false;
	dataSource: MatTableDataSource<Memo> = new MatTableDataSource(this.data);
	
	// Filtres actuellement appliques aux memos
	private appliedFilters!: MemosFilters;

	// Referentiels pour l'affichage des tableaux
	memoCategories!: Map<number, MemoCategory>;
	memoSubCategories!: Map<number, MemoSubCategory>;
	memoLevels!: Map<number, MemoLevel>;
	parcs!: Map<number, ParcTLR>;
	ouvrages!: Map<number, OuvrageTLR>;

	constructor( //
		private dialog: MatDialog,
		@Inject(MemoService) private memoService: MemoService,
		@Inject(JSR310ConversionService) public jsr310Service: JSR310ConversionService,
		@Inject(StaticTypeService) private staticTypeService: StaticTypeService,
		@Inject(ParcService) private parcService: ParcService,
		@Inject(OuvrageService) private ouvrageService: OuvrageService
	) {
		this.staticTypeService.getMemoCategoryMapById().subscribe(
			(map: Map<number, MemoCategory>) => this.memoCategories = map
		);
		this.staticTypeService.getMemoSubCategoryMapById().subscribe(
			(map: Map<number, MemoSubCategory>) => this.memoSubCategories = map
		);
		this.staticTypeService.getMemoLevelMapById().subscribe(
			(map: Map<number, MemoLevel>) => this.memoLevels = map
		);
		this.parcService.getParcTLRMapById().subscribe(
			(map: Map<number, ParcTLR>) => this.parcs = map
		);
		this.ouvrageService.getOuvrageTLRMapById().subscribe(
			(map: Map<number, OuvrageTLR>) => this.ouvrages = map
		);
	}

	// Elements injectes necessaires a la gestion du tableau
	@ViewChild(MatPaginator) paginator!: MatPaginator;
	@ViewChild(MatSort) sort!: MatSort;
	
	// Lien vers la popup d'edition de memo
	@ViewChild(EditMemoDialogComponent) editPopup!: EditMemoDialogComponent;

	// Liaison de l'observable alertante des changements de filtres
	private filterRelay = new CommandRelay<MemosFilters>(filters => {
		this.appliedFilters = filters;
		this.reloadMemoData();
	});
	@Input() set filtersSubject(obs: Observable<MemosFilters> | undefined) {
		this.filterRelay.register(obs);
	}

	ngAfterViewInit() {
		// Initialisation du dataSource
		this.dataSource.paginator = this.paginator;
		this.dataSource.sort = this.sort;

		// Preparation de l'accesseur particulier pour le tri des proprietes LocalDate
		this.dataSource.sortingDataAccessor = (item, property) => {
			switch (property) {
				case 'categorie': {
					return this.getCategorieLabel(item);
				}
				case 'niveau': {
					return this.getNiveauLabel(item);
				}
				case 'parc': {
					return this.getParcLabel(item);
				}
				case 'ouvrages': {
					return this.getOuvragesLabel(item);
				}
				case 'dateDebut': {
					const value = item.dateDebut;
					const valueAsJSDate = this.jsr310Service.fromLocalDate(value);
					return valueAsJSDate;
				}
				case 'dateFin': {
					const value = item.dateFin;
					const valueAsJSDate = value ? this.jsr310Service.fromLocalDate(value) : null;
					return valueAsJSDate;
				}
				default: {
					// NB: etrangement, juste faire "item[property]" cause une erreur de compilation
					const value = (item as { [key: string]: any })[property];
					return value;
				}
			}
		};
	}

	getCategorieLabel(memo: Memo): string {
		const categorie = this.memoCategories.get(memo.categorieId);
		if (!categorie) {
			return '#error';
		}

		if (!memo.sousCategorieId) {
			return categorie.label;
		}

		const sousCategorie = this.memoSubCategories.get(memo.sousCategorieId);
		if (sousCategorie) {
			return categorie.label + ' - ' + sousCategorie.label;
		} else {
			return categorie.label + ' - #error';
		}
	}

	getNiveauLabel(memo: Memo): string {
		const niveau = this.memoLevels.get(memo.niveauId);
		if (niveau) {
			return niveau.label;
		}
		return '#error';
	}

	getParcLabel(memo: Memo): string {
		const parc = this.parcs.get(memo.parcId);
		if (parc) {
			return parc.code + " (" + parc.description + ")";
		}
		return '#error';
	}

	getOuvragesLabel(memo: Memo): string {
		const ouvrageIds = memo.ouvrageIds;
		if (!ouvrageIds || ouvrageIds.length === 0) {
			return "Tous";
		} else if (this.ouvrages) {
			const labels = ouvrageIds
				.map((id: number) => this.ouvrages.get(id))
				.map((ouvrage: OuvrageTLR | undefined) => ouvrage ? ouvrage.nom : '#error')
				;
			return labels.join(", ");
		} else {
			// Ce cas est atteint si l'interface requiere un label alors que les ouvrages ne sont pas encore charges
			// C'est une situation normalement temporaire, on renvoie une valeur nulle pour eviter une erreur dans les logs
			return "<unknown>";
		}
	}

	private reloadMemoData() {
		this.memoService.getMemosByFilters(this.appliedFilters).subscribe(
			(results: Memo[]) => this.displayMemos(results)
		);
	}

	private displayMemos(data: Memo[]) {
		this.data = data;

		// Gestion de la limitation du nombre de resultats
		this.maxSizeExceeded = data.length > this.maxSize;
		if (this.maxSizeExceeded) {
			this.data.splice(this.maxSize);
		}

		// Mise a jour du dataSource
		this.dataSource.data = this.data;

		// Retrait de l'indicateur de chargement
		this.isLoading = false;
	}
	
	parcInactif(memo: Memo) {
		let parc = this.parcs.get(memo.parcId);
		return !parc || !parc.actif;
	}

	/////////////////////////////////////
	// Gestion des actions utilisateur //
	/////////////////////////////////////

	onMemoCreationRequested() {
		this.editPopup.openForCreation();
	}

	onMemoCreate() {
		// Apres creation d'un nouveau memo, recharger les donnees
		this.reloadMemoData();
	}

	onMemoUpdateRequested(memo: Memo) {
		let parc = this.parcs.get(memo.parcId);
		if (!parc || !parc.actif || memo.archived) {
			return;
		}

		this.editPopup.openForUpdate(memo);
	}

	onMemoUpdate() {
		// Apres modification d'un memo, recharger les donnees
		// NB: au cas ou cela deviendrait lent, on peut ici garder du temps de chargement en trouver le memo par son id dans le tableau et en substituant sa nouvelle version
		this.reloadMemoData();
	}

	onMemoDeletionRequested(memo: Memo) {
		const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
			width: '400px',
			data: {
				message: 'Confirmez-vous la suppression de ce mémo ?'
			}
		});

		dialogRef.afterClosed().subscribe(
			result => {
				if (result) {
					this.memoService.deleteMemo(memo.id) //
						.pipe(
							catchError(
								(err: any, _caught: Observable<any>) => {
									alert("Erreur lors de la suppression du memo (Erreur " + err.status + ")");
									return of("error");
								}
							)
						)
						.subscribe(
							(result: any) => {
								if (result != "Error") {
									// Rafraichir l'affichage
									this.reloadMemoData();

									// Afficher le confirmation de suppression
									window.alert("Le mémo " + memo.id + " a été supprimé");
								}
							}
						);
				}
			}
		);
	}

	onMemoArchivingRequested(memo: Memo) {
		if (memo.archived) {
			return;
		}

		this.editPopup.openForArchive(memo);
	}

	onMemoArchive() {
		// Apres archivage d'un memo, recharger les donnees
		// NB: au cas ou cela deviendrait lent, on peut ici garder du temps de chargement en trouver le memo par son id dans le tableau et en substituant sa nouvelle version
		this.reloadMemoData();
	}
}